import moment from "moment";
import { CATEGORIES, PRE_1997_CONSTRUCTION_CUTOFF_DATE } from "../calc/shared/haklaiConstants";
import {
  getHaklaiComputedAcquisitionByPart,
  getHaklaiLogicalPropertyPartsData,
  getRenderablePropertyPartRows,
  isHaklaiPre1997MinimalZabanRelevantForPart
} from "../calc/shared/haklaiFlowHelpers";
import { syncHaklaiAutomaticPurchaseDepreciationSelection } from "../calc/shared/haklaiPurchaseDepreciationState";
import {
  getHaklaiOwnershipStartDate,
  getHaklaiPurchaseDepreciationStartDate,
  isHaklaiPre1997AcquisitionDateEligible
} from "../calc/preCalc/logic/haklaiPreCalcHelpers";
import { getPropertyPartsValidationError } from "../calc/propertyParts/logic/haklaiPropertyPartsGates";
import {
  getHaklaiExpenseDepreciationSummary,
  getHaklaiExpenseValidationState,
  isHaklaiExpenseStarted
} from "../calc/expenses/logic/haklaiExpensesHelpers";
import {
  getHaklaiPropertyDepreciationValidationError,
  getHaklaiResidentialRentalProfile,
  hasHaklaiManualForm11DepreciationInput,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  isHaklaiManualPurchaseDepreciationAllowed
} from "../calc/shared/haklaiDepreciationHelpers";
import {
  HAKLAI_MAX_EXPENSE_DEPRECIATION_SELECTIONS_PER_PART
} from "../calc/expenses/logic/haklaiExpenseDepreciationPolicy";
import { HAKLAI_EXPENSE_ERRORS } from "../calc/expenses/haklaiExpenseTexts";
import { getHaklaiPrisaSubmitError } from "../calc/prisa/haklaiPrisaValidation";
import { getHaklaiPreCalcSubmitErrorMessage } from "../calc/preCalc/logic/haklaiPreCalcValidation";
import { INHERITANCE_TESTATOR_BASIS_START_DATE } from "./haklaiPrisaDependencies";
import {
  getHaklaiAdditionalDeductionLabel,
  getHaklaiExpenseDepreciationSelectionLimitMessage,
  getHaklaiMissingUsdRateMessage,
  getHaklaiNumberedDeductionLabel,
  getHaklaiPrefixedValidationMessage,
  getHaklaiPurchaseValidationMessage,
  HAKLAI_DOMAIN_VALIDATION_ERRORS,
  HAKLAI_DOMAIN_VALIDATION_LABELS
} from "./haklaiValidationTexts";

export const getPre1997ConstructionValidationError = (preCalc = {}, propertyPartsData = {}) => {
  const checkedRows = Object.entries(propertyPartsData || {}).filter(
    ([, part]) => part?.constructionsDoneBeforeApril1997 === true
  );
  if (!checkedRows.length) return null;
  if (checkedRows.length > 1) {
    return HAKLAI_DOMAIN_VALIDATION_ERRORS.multiplePre1997Homes;
  }

  const hasZaban = preCalc.hasZaban === true;
  if (!isHaklaiPre1997AcquisitionDateEligible(preCalc)) {
    return HAKLAI_DOMAIN_VALIDATION_ERRORS.pre1997AcquisitionDateNotEligible;
  }
  const hasIrrelevantCheckedRow = checkedRows.some(
    ([, part]) => !isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
  );
  if (hasIrrelevantCheckedRow) {
    return HAKLAI_DOMAIN_VALIDATION_ERRORS.pre1997IrrelevantHome;
  }

  const way = preCalc.wayOfGettingProperty;
  const acquisitionDetails = preCalc.acquisitionDetails || {};

  if (way === "purchase" || way === "gift") {
    if (
      acquisitionDetails.deduDate
      && moment(acquisitionDetails.deduDate).isSameOrAfter(moment(PRE_1997_CONSTRUCTION_CUTOFF_DATE), "day")
    ) {
      return HAKLAI_DOMAIN_VALIDATION_ERRORS.purchaseOrGiftPre1997DateNotEligible;
    }
  }

  if (
    way === "inheritance"
    && acquisitionDetails.testatorPassingDate
    && moment(acquisitionDetails.testatorPassingDate).isSameOrAfter(moment(INHERITANCE_TESTATOR_BASIS_START_DATE), "day")
    && acquisitionDetails.testatorBoughtPropertyDate
    && moment(acquisitionDetails.testatorBoughtPropertyDate).isSameOrAfter(moment(PRE_1997_CONSTRUCTION_CUTOFF_DATE), "day")
  ) {
    return HAKLAI_DOMAIN_VALIDATION_ERRORS.inheritancePre1997DateNotEligible;
  }

  return null;
};

export const validateHaklaiExpensesForSubmit = (sourceCalc = {}) => {
  const expenses = sourceCalc.mainCalc?.expenses || [];
  if (!expenses.length) return null;
  const soldPropertyDate = sourceCalc.preCalc?.soldPropertyDate;
  const ownershipStartDate = getHaklaiOwnershipStartDate(sourceCalc.preCalc || {});
  const propertyPartsData = getHaklaiLogicalPropertyPartsData(
    sourceCalc.mainCalc?.propertyPartsData || {},
    sourceCalc.preCalc?.totalPropertyCost,
    sourceCalc.preCalc?.hasZaban === true
  );
  const expenseCountByPart = {};

  for (let i = 0; i < expenses.length; i++) {
    const expense = expenses[i] || {};
    if (!isHaklaiExpenseStarted(expense)) {
      continue;
    }

    const targetLabel = propertyPartsData[expense.targetPartKey]?.label;
    if (expense.targetPartKey) {
      expenseCountByPart[expense.targetPartKey] = (expenseCountByPart[expense.targetPartKey] || 0) + 1;
    }
    const prefix = targetLabel
      ? getHaklaiAdditionalDeductionLabel(targetLabel, expenseCountByPart[expense.targetPartKey] || 1)
      : getHaklaiNumberedDeductionLabel(i);

    if (!expense.expenseType || !String(expense.expenseType).trim()) {
      return getHaklaiPrefixedValidationMessage(prefix, HAKLAI_EXPENSE_ERRORS.expenseTypeSubmitRequired);
    }
    const validationState = getHaklaiExpenseValidationState({
      expense,
      soldPropertyDate,
      ownershipStartDate,
      propertyPartsData,
      hasZaban: sourceCalc.preCalc?.hasZaban === true
    });
    if (validationState.fieldErrors.expenseDate) {
      return getHaklaiPrefixedValidationMessage(prefix, validationState.fieldErrors.expenseDate);
    }
    if (validationState.fieldErrors.amount) {
      return getHaklaiPrefixedValidationMessage(prefix, validationState.fieldErrors.amount);
    }
    if (validationState.fieldErrors.expenseCurrency) {
      return getHaklaiPrefixedValidationMessage(prefix, validationState.fieldErrors.expenseCurrency);
    }
    if (validationState.fieldErrors.targetPartKey) {
      return getHaklaiPrefixedValidationMessage(prefix, validationState.fieldErrors.targetPartKey);
    }
    if (validationState.fieldErrors.zabanAttribution) {
      return getHaklaiPrefixedValidationMessage(prefix, validationState.fieldErrors.zabanAttribution);
    }
  }

  return null;
};

export const validateHaklaiPropertyDepreciationProfiles = (sourceCalc = {}) => {
  const soldPropertyDate = sourceCalc.preCalc?.soldPropertyDate;
  const purchaseDepreciationStartDate = getHaklaiPurchaseDepreciationStartDate(sourceCalc.preCalc || {});
  const propertyPartsData = getHaklaiLogicalPropertyPartsData(
    sourceCalc.mainCalc?.propertyPartsData || {},
    sourceCalc.preCalc?.totalPropertyCost,
    sourceCalc.preCalc?.hasZaban === true
  );
  const residentialRentalByPart = sourceCalc.mainCalc?.residentialRentalByPart || {};
  const purchaseDepreciationByPart = sourceCalc.mainCalc?.purchaseDepreciationByPart || {};
  const renderableRows = getRenderablePropertyPartRows(propertyPartsData);

  for (let index = 0; index < renderableRows.length; index += 1) {
    const [partKey, part] = renderableRows[index];
    const label = part?.label || HAKLAI_DOMAIN_VALIDATION_LABELS.propertyPart;
    const residentialRentalProfile = part?.category === CATEGORIES.RESIDENTIAL
      ? getHaklaiResidentialRentalProfile({
        partKey,
        part,
        residentialRentalByPart,
        purchaseDepreciationByPart
      })
      : {};
    const rawManualProfile = purchaseDepreciationByPart[partKey];
    const isManualPurchaseAllowed = isHaklaiManualPurchaseDepreciationAllowed(part);
    const manualProfile = (
      isManualPurchaseAllowed
      && hasHaklaiManualForm11DepreciationInput(rawManualProfile)
    )
      ? rawManualProfile
      : {};
    const hasResidentialRentalProfile = Boolean(Object.keys(residentialRentalProfile || {}).length);
    const hasManualProfile = Boolean(Object.keys(manualProfile || {}).length);

    if (hasResidentialRentalProfile && hasManualProfile) {
      return getHaklaiPurchaseValidationMessage(
        label,
        HAKLAI_DOMAIN_VALIDATION_ERRORS.purchaseDepreciationModeConflict
      );
    }

    if (hasResidentialRentalProfile) {
      const validationError = getHaklaiPropertyDepreciationValidationError({
        part,
        depreciation: residentialRentalProfile,
        soldPropertyDate,
        ownershipStartDate: purchaseDepreciationStartDate,
        modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      });
      if (validationError) return getHaklaiPurchaseValidationMessage(label, validationError);
    }

    if (hasManualProfile) {
      const validationError = getHaklaiPropertyDepreciationValidationError({
        part,
        depreciation: manualProfile,
        soldPropertyDate,
        ownershipStartDate: purchaseDepreciationStartDate,
        modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
      });
      if (validationError) return getHaklaiPurchaseValidationMessage(label, validationError);
    }
  }

  return null;
};

export const validateHaklaiExpenseDepreciation = (sourceCalc = {}) => {
  const expenses = sourceCalc.mainCalc?.expenses || [];
  const propertyPartsData = getHaklaiLogicalPropertyPartsData(
    sourceCalc.mainCalc?.propertyPartsData || {},
    sourceCalc.preCalc?.totalPropertyCost,
    sourceCalc.preCalc?.hasZaban === true
  );
  const residentialRentalByPart = sourceCalc.mainCalc?.residentialRentalByPart || {};
  const purchaseDepreciationByPart = sourceCalc.mainCalc?.purchaseDepreciationByPart || {};
  const expenseCountByPart = {};
  const automaticPurchaseSelection = syncHaklaiAutomaticPurchaseDepreciationSelection({
    currentSelection: sourceCalc.mainCalc?.purchaseDepreciationSelectionByPart || {},
    logicalPropertyPartsData: propertyPartsData,
    residentialRentalByPart,
    purchaseDepreciationByPart,
    soldPropertyDate: sourceCalc.preCalc?.soldPropertyDate || "",
    purchaseDepreciationStartDate: getHaklaiPurchaseDepreciationStartDate(sourceCalc.preCalc || {}),
    computedAcquisitionByPart: getHaklaiComputedAcquisitionByPart(
      propertyPartsData,
      sourceCalc.preCalc?.acquisitionDetails?.amount
    )
  });
  const depreciationSelectionCountByPart = Object.keys(
    automaticPurchaseSelection.purchaseDepreciationSelectionByPart
  ).reduce((acc, partKey) => {
    acc[partKey] = 1;
    return acc;
  }, {});

  for (let index = 0; index < expenses.length; index += 1) {
    const expense = expenses[index] || {};
    if (!isHaklaiExpenseStarted(expense)) continue;

    const targetLabel = propertyPartsData[expense.targetPartKey]?.label;
    if (expense.targetPartKey) {
      expenseCountByPart[expense.targetPartKey] = (expenseCountByPart[expense.targetPartKey] || 0) + 1;
    }
    const prefix = targetLabel
      ? getHaklaiAdditionalDeductionLabel(targetLabel, expenseCountByPart[expense.targetPartKey] || 1)
      : getHaklaiNumberedDeductionLabel(index);

    const depreciationSummary = getHaklaiExpenseDepreciationSummary({
      expense,
      propertyPartsData,
      residentialRentalByPart,
      purchaseDepreciationByPart,
      soldPropertyDate: sourceCalc.preCalc?.soldPropertyDate || "",
      ownershipStartDate: getHaklaiOwnershipStartDate(sourceCalc.preCalc || {})
    });
    const invalidAllocation = depreciationSummary.allocations.find((allocation) => allocation.validationError);
    if (invalidAllocation) {
      return getHaklaiPrefixedValidationMessage(prefix, invalidAllocation.validationError);
    }

    for (let allocationIndex = 0; allocationIndex < depreciationSummary.allocations.length; allocationIndex += 1) {
      const allocation = depreciationSummary.allocations[allocationIndex];
      if (!allocation?.partKey || !allocation.hasDepreciation) continue;
      depreciationSelectionCountByPart[allocation.partKey] = (
        depreciationSelectionCountByPart[allocation.partKey] || 0
      ) + 1;
      if (
        depreciationSelectionCountByPart[allocation.partKey]
        > HAKLAI_MAX_EXPENSE_DEPRECIATION_SELECTIONS_PER_PART
      ) {
        const label = allocation.part?.label || targetLabel || HAKLAI_DOMAIN_VALIDATION_LABELS.propertyPart;
        return getHaklaiExpenseDepreciationSelectionLimitMessage(label);
      }
    }
  }

  return null;
};

export const validateHaklaiDepreciationsForSave = (sourceCalc = {}) => (
  validateHaklaiPropertyDepreciationProfiles(sourceCalc)
  || validateHaklaiExpenseDepreciation(sourceCalc)
  || null
);

export const validateHaklaiForAnalyze = ({ calc = {}, conversionDate, usdRate }) => {
  const preCalcErr = getHaklaiPreCalcSubmitErrorMessage(calc.preCalc);
  if (preCalcErr) return preCalcErr;

  const acq = calc.preCalc.acquisitionDetails || {};
  if (acq.deduCurrency === "usd") {
    if (!usdRate) {
      const dateLabel =
        conversionDate
        || acq.deduDate
        || acq.testatorBoughtPropertyDate
        || acq.testatorPassingDate
        || "";
      return getHaklaiMissingUsdRateMessage(dateLabel);
    }
  }

  const pre1997ConstructionErr = getPre1997ConstructionValidationError(
    calc.preCalc,
    calc.mainCalc?.propertyPartsData || {}
  );
  if (pre1997ConstructionErr) return pre1997ConstructionErr;

  const propertyPartsErr = getPropertyPartsValidationError({
    preCalc: calc.preCalc,
    propertyPartsData: calc.mainCalc.propertyPartsData || {},
    totalPropertyCost: Number(calc.preCalc.totalPropertyCost)
  });
  if (propertyPartsErr) return propertyPartsErr;

  const propertyDepreciationErr = validateHaklaiPropertyDepreciationProfiles(calc);
  if (propertyDepreciationErr) return propertyDepreciationErr;

  const expensesErr = validateHaklaiExpensesForSubmit(calc);
  if (expensesErr) return expensesErr;

  const expenseDepreciationErr = validateHaklaiExpenseDepreciation(calc);
  if (expenseDepreciationErr) return expenseDepreciationErr;

  const prisaErr = getHaklaiPrisaSubmitError(calc);
  if (prisaErr) return prisaErr;

  return null;
};
